import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import type { Setlist, SetlistSection, SetlistItem, SectionSortMode } from '@/types'

const normalizeSortMode = (sortMode: string | null | undefined): SectionSortMode => {
  if (sortMode === 'year_desc' || sortMode === 'year_asc' || sortMode === 'manual') return sortMode
  // Backward compatibility for older persisted modes.
  if (sortMode === 'year') return 'year_asc'
  return 'manual'
}

const compareItems = (sort: SectionSortMode, a: SetlistItem, b: SetlistItem) => {
  if (sort === 'year_asc') return (a.song?.year ?? 9999) - (b.song?.year ?? 9999)
  if (sort === 'year_desc') return (b.song?.year ?? 0) - (a.song?.year ?? 0)
  return 0
}

export const useSetlists = (bandId: string | undefined) => {
  const [setlists, setSetlists] = useState<Setlist[]>([])
  const [loading, setLoading] = useState(true)

  const fetchSetlists = useCallback(async () => {
    if (!bandId) return
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('setlists')
        .select('*')
        .eq('band_id', bandId)
        .order('date', { ascending: false })
      if (error) throw error
      setSetlists(data ?? [])
    } finally {
      setLoading(false)
    }
  }, [bandId])

  useEffect(() => {
    void fetchSetlists()
  }, [fetchSetlists])

  const createSetlist = async (setlist: { name: string; venue: string; date: string; notes?: string }) => {
    const { data, error } = await supabase
      .from('setlists')
      .insert({ ...setlist, band_id: bandId })
      .select()
      .single()
    if (error) throw error
    await fetchSetlists()
    return data as Setlist
  }

  const updateSetlist = async (id: string, updates: Partial<Pick<Setlist, 'name' | 'venue' | 'date' | 'notes'>>) => {
    const { error } = await supabase.from('setlists').update(updates).eq('id', id)
    if (error) throw error
    await fetchSetlists()
  }

  const deleteSetlist = async (id: string) => {
    const { error } = await supabase.from('setlists').delete().eq('id', id)
    if (error) throw error
    await fetchSetlists()
  }

  const copySetlist = async (id: string) => {
    const { data: original, error: fetchErr } = await supabase
      .from('setlists')
      .select('*')
      .eq('id', id)
      .single()
    if (fetchErr) throw fetchErr

    const { data: newSetlist, error: insertErr } = await supabase
      .from('setlists')
      .insert({
        band_id: original.band_id,
        name: `${original.name} (Copy)`,
        venue: original.venue,
        date: original.date,
        notes: original.notes,
      })
      .select()
      .single()
    if (insertErr) throw insertErr

    const { data: sections, error: secErr } = await supabase
      .from('setlist_sections')
      .select('*')
      .eq('setlist_id', id)
      .order('position')
    if (secErr) throw secErr

    for (const section of sections ?? []) {
      const { data: newSection, error: secInsertErr } = await supabase
        .from('setlist_sections')
        .insert({
          setlist_id: newSetlist.id,
          name: section.name,
          sort_mode: section.sort_mode,
          position: section.position,
        })
        .select()
        .single()
      if (secInsertErr) throw secInsertErr

      const { data: items, error: itemErr } = await supabase
        .from('setlist_items')
        .select('*')
        .eq('section_id', section.id)
        .order('position')
      if (itemErr) throw itemErr

      if (items && items.length > 0) {
        const { error: itemInsertErr } = await supabase
          .from('setlist_items')
          .insert(items.map((item) => ({
            section_id: newSection.id,
            song_id: item.song_id,
            position: item.position,
          })))
        if (itemInsertErr) throw itemInsertErr
      }
    }

    await fetchSetlists()
    return newSetlist as Setlist
  }

  return { setlists, loading, refetch: fetchSetlists, createSetlist, updateSetlist, deleteSetlist, copySetlist }
}

export const useSetlistDetail = (setlistId: string | undefined) => {
  const [sections, setSections] = useState<(SetlistSection & { items: SetlistItem[] })[]>([])
  const [loading, setLoading] = useState(true)
  const suppressRealtimeCountRef = useRef(0)
  const pendingRealtimeRefreshRef = useRef(false)
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetchDetail = useCallback(async (initial = false) => {
    if (!setlistId) return
    if (initial) setLoading(true)
    try {
      const { data: secs, error: secErr } = await supabase
        .from('setlist_sections')
        .select('*')
        .eq('setlist_id', setlistId)
        .order('position')
      if (secErr) throw secErr

      const sectionIds = (secs ?? []).map((s) => s.id)
      let items: SetlistItem[] = []
      if (sectionIds.length > 0) {
        const { data: itemData, error: itemErr } = await supabase
          .from('setlist_items')
          .select('*, song:songs(*)')
          .in('section_id', sectionIds)
          .order('position')
        if (itemErr) throw itemErr
        items = itemData ?? []
      }

      const merged = (secs ?? []).map((sec) => ({
        ...sec,
        sort_mode: normalizeSortMode(sec.sort_mode),
        items: items.filter((i) => i.section_id === sec.id),
      }))
      setSections(merged)
    } finally {
      setLoading(false)
    }
  }, [setlistId])

  useEffect(() => {
    void fetchDetail(true)
  }, [fetchDetail])

  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
    }
  }, [])

  const scheduleDetailRefresh = useCallback(() => {
    if (suppressRealtimeCountRef.current > 0) {
      pendingRealtimeRefreshRef.current = true
      return
    }
    if (refreshTimerRef.current) return

    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null
      void fetchDetail()
    }, 90)
  }, [fetchDetail])

  const beginRealtimeSuppression = () => {
    suppressRealtimeCountRef.current += 1
  }

  const endRealtimeSuppression = async () => {
    suppressRealtimeCountRef.current = Math.max(0, suppressRealtimeCountRef.current - 1)
    if (suppressRealtimeCountRef.current === 0 && pendingRealtimeRefreshRef.current) {
      pendingRealtimeRefreshRef.current = false
      await fetchDetail()
    }
  }

  // Realtime subscription for live sync
  useEffect(() => {
    if (!setlistId) return

    const channel = supabase
      .channel(`setlist-${setlistId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'setlist_sections', filter: `setlist_id=eq.${setlistId}` }, () => {
        scheduleDetailRefresh()
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'setlist_items' }, () => {
        scheduleDetailRefresh()
      })
      .subscribe()

    return () => {
      void supabase.removeChannel(channel)
    }
  }, [setlistId, scheduleDetailRefresh])

  const addSection = async (name: string) => {
    const nextPos = sections.length
    const { error } = await supabase
      .from('setlist_sections')
      .insert({ setlist_id: setlistId, name, position: nextPos })
    if (error) throw error
    await fetchDetail()
  }

  const removeSection = async (sectionId: string) => {
    const { error } = await supabase.from('setlist_sections').delete().eq('id', sectionId)
    if (error) throw error
    await fetchDetail()
  }

  const addItem = async (sectionId: string, songId: string) => {
    const section = sections.find((s) => s.id === sectionId)
    const nextPos = section ? section.items.length : 0
    const { error } = await supabase
      .from('setlist_items')
      .insert({ section_id: sectionId, song_id: songId, position: nextPos })
    if (error) throw error
    await fetchDetail()
  }

  const removeItem = async (itemId: string) => {
    // Optimistic: remove from local state immediately
    setSections((prev) => prev.map((sec) => ({
      ...sec,
      items: sec.items.filter((i) => i.id !== itemId),
    })))
    const { error } = await supabase.from('setlist_items').delete().eq('id', itemId)
    if (error) {
      await fetchDetail() // rollback on error
      throw error
    }
  }

  const moveItem = async (itemId: string, newSectionId: string, newPosition: number) => {
    const { error } = await supabase
      .from('setlist_items')
      .update({ section_id: newSectionId, position: newPosition })
      .eq('id', itemId)
    if (error) throw error
    await fetchDetail()
  }

  const updateSectionSortMode = async (sectionId: string, sortMode: SectionSortMode) => {
    // Optimistic: persist selected sort mode locally immediately.
    setSections((prev) => prev.map((sec) => (
      sec.id === sectionId ? { ...sec, sort_mode: sortMode } : sec
    )))

    const { error } = await supabase
      .from('setlist_sections')
      .update({ sort_mode: sortMode })
      .eq('id', sectionId)
    if (error) {
      await fetchDetail() // rollback on error
      throw error
    }
  }

  const sortSectionItems = async (sectionId: string, sortMode: SectionSortMode) => {
    if (sortMode === 'manual') return

    const { data: itemData, error } = await supabase
      .from('setlist_items')
      .select('*, song:songs(*)')
      .eq('section_id', sectionId)
      .order('position')
    if (error) throw error

    const items = (itemData ?? []) as SetlistItem[]
    const sorted = [...items].sort((a, b) => compareItems(sortMode, a, b))

    const unchanged = sorted.every((item, idx) => item.id === items[idx]?.id)
    if (unchanged) return

    await reorderItems(sectionId, sorted.map((i) => i.id))
  }

  const reorderItems = async (sectionId: string, orderedItemIds: string[]) => {
    // Optimistic: reorder in local state immediately
    setSections((prev) => prev.map((sec) => {
      if (sec.id !== sectionId) return sec
      const itemMap = new Map(sec.items.map((i) => [i.id, i]))
      return {
        ...sec,
        items: orderedItemIds.map((id, idx) => {
          const item = itemMap.get(id)!
          return { ...item, position: idx }
        }),
      }
    }))

    // Suppress realtime refreshes while a multi-row reorder is in-flight.
    beginRealtimeSuppression()

    // Persist to DB in background
    try {
      const updates = orderedItemIds.map((id, idx) =>
        supabase.from('setlist_items').update({ position: idx }).eq('id', id),
      )
      const results = await Promise.all(updates)
      const failed = results.some((r) => r.error)
      if (failed) {
        await fetchDetail() // rollback on error
        return
      }

      // One final sync after the batch write completes.
      await fetchDetail()
    } finally {
      await endRealtimeSuppression()
    }
  }

  return {
    sections,
    loading,
    refetch: fetchDetail,
    addSection,
    removeSection,
    addItem,
    removeItem,
    moveItem,
    reorderItems,
    updateSectionSortMode,
    sortSectionItems,
  }
}
